/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable @typescript-eslint/no-explicit-any */
import type { CSSProperties, FC } from 'react'
import React, { useCallback, useEffect, useRef } from 'react'
import { Transition as TransitionComponent } from 'react-transition-group'
import type { TransitionStatus } from 'react-transition-group/Transition'

import type { GrowProps } from './types'
import useForkRef from '../../../hooks/use-fork-ref'
import type { Theme } from '../../../theme-provider/types'
import { useSafeThemeUI } from '../../../theme-provider/use-theme-ui'
import { duration as _duration } from '../../../themes/theme-keys/transitions'
import { isNumber } from '../../../utils/assertion'
import { getTransitionProps, reflow } from '../transitions/utils'

function getScale(value: number) {
  return `scale(${value}, ${value ** 2})`
}

const styles: Partial<Record<TransitionStatus, CSSProperties>> = {
  entering: {
    opacity: 1,
    transform: getScale(1)
  },
  entered: {
    opacity: 1,
    transform: 'none'
  }
}

/**
 * The Grow transition is used by the [Tooltip](/story/components-tooltip--tooltip) and
 * [Popover](/components/popover/) components.
 * It uses [react-transition-group](https://github.com/reactjs/react-transition-group) internally.
 */
// no way around this ts-ignore that I can find compound pattern does not seem play well with React.forwardRef
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const Grow: FC<GrowProps> & {
  supportAuto: boolean
} = React.forwardRef<HTMLElement, GrowProps & { theme?: Theme }>(
  function Grow(props, ref) {
    const {
      appear = true,
      children,
      easing,
      in: inProp,
      onEnter,
      onExit,
      style,
      timeout = _duration.standard,
      transitionStyles = {},
      ...other
    } = props
    const timer = useRef<any>()
    const autoTimeout = useRef<number>()
    const handleRef = children
      ? useForkRef((children as any).ref, ref)
      : useRef<HTMLElement>()
    const theme = useSafeThemeUI(props)

    const handleEnter = useCallback(
      (node: HTMLElement, isAppearing: boolean) => {
        reflow(node) // So the animation always start from the start.

        const { duration: transitionDuration, delay } = getTransitionProps(
          {
            style,
            timeout: timeout !== 'auto' ? timeout : _duration.standard,
            easing
          },
          {
            mode: 'enter'
          }
        )

        let duration
        if (timeout === 'auto') {
          duration = theme.transitions
            ? theme.transitions.getAutoHeightDuration(node.clientHeight)
            : 500
          autoTimeout.current = duration
        } else {
          duration = transitionDuration
        }

        node.style.transition = [
          theme.transitions
            ? theme.transitions.create('opacity', {
                duration,
                delay
              })
            : '',
          theme.transitions && isNumber(duration)
            ? theme.transitions.create('transform', {
                duration: duration * 0.666,
                delay
              })
            : ''
        ].join(',')

        if (onEnter) {
          onEnter(node, isAppearing)
        }
      },
      [easing, onEnter, style, theme.transitions, timeout]
    )

    const handleExit = useCallback(
      (node: HTMLElement) => {
        const { duration: transitionDuration, delay } = getTransitionProps(
          {
            style,
            timeout: timeout !== 'auto' ? timeout : _duration.standard,
            easing
          },
          {
            mode: 'exit'
          }
        )

        let duration
        if (timeout === 'auto') {
          duration = theme.transitions
            ? theme.transitions.getAutoHeightDuration(node.clientHeight)
            : 500
          autoTimeout.current = duration
        } else {
          duration = transitionDuration
        }

        node.style.transition = [
          theme.transitions
            ? theme.transitions.create('opacity', {
                duration,
                delay
              })
            : '',
          theme.transitions && isNumber(duration)
            ? theme.transitions.create('transform', {
                duration: duration * 0.666,
                delay: delay || duration * 0.333
              })
            : ''
        ].join(',')

        node.style.opacity = '0'
        node.style.transform = getScale(0.75)

        if (onExit) {
          onExit(node)
        }
      },
      [easing, onExit, style, theme.transitions, timeout]
    )

    const addEndListener = useCallback(
      (_: any, next: () => void) => {
        if (timeout === 'auto') {
          timer.current = setTimeout(next, autoTimeout.current || 0)
        }
      },
      [timeout]
    )

    useEffect(() => {
      return () => {
        clearTimeout(timer.current)
      }
    }, [])

    return (
      <TransitionComponent
        appear={appear}
        in={inProp}
        onEnter={handleEnter}
        onExit={handleExit}
        addEndListener={addEndListener}
        timeout={timeout === 'auto' ? 500 : timeout}
        {...other}
      >
        {
          // @ts-ignore
          (state: TransitionStatus, childProps: any) => {
            return children
              ? React.cloneElement(children, {
                  style: {
                    opacity: 0,
                    transform: getScale(0.75),
                    visibility:
                      state === 'exited' && !inProp ? 'hidden' : undefined,
                    ...styles[state],
                    ...transitionStyles[state],
                    ...style,
                    ...children.props.style
                  },
                  ref: handleRef,
                  ...childProps
                })
              : null
          }
        }
      </TransitionComponent>
    )
  }
)

Grow.supportAuto = true

export default Grow
